import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import * as LucideIcons from "lucide-react";
import { Search, Package, ArrowRight } from "lucide-react";

const DIFF_COLORS = {
  facile: { bg: "rgba(34,197,94,0.12)", color: "#15803d" },
  media: { bg: "rgba(234,179,8,0.15)", color: "#a16207" },
  avanzata: { bg: "rgba(200,16,46,0.12)", color: "#C8102E" },
};

export default function Soluzioni() {
  const [esigenze, setEsigenze] = useState([]);
  const [projects, setProjects] = useState([]);
  const [activeEsigenza, setActiveEsigenza] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [params] = useSearchParams();
  const sectorFilter = params.get("sector") || "";

  useEffect(() => {
    api.get("/oss/esigenze").then((r) => setEsigenze(r.data.items || []));
  }, []);

  useEffect(() => {
    setLoading(true);
    const p = {};
    if (activeEsigenza) p.esigenza = activeEsigenza;
    if (search) p.q = search;
    if (sectorFilter) p.sector = sectorFilter;
    const t = setTimeout(() => {
      api.get("/oss/projects", { params: p }).then((r) => {
        setProjects(r.data.items || []);
        setLoading(false);
      });
    }, 200);
    return () => clearTimeout(t);
  }, [activeEsigenza, search, sectorFilter]);

  return (
    <div>
      <div className="adr-overline mb-3">Open Source Hub</div>
      <h1 className="serif text-3xl sm:text-4xl font-semibold tracking-tight text-[#1A1A1A] mb-2">
        Soluzioni pronte per la tua azienda
      </h1>
      <p className="text-[#555555] mb-8 max-w-2xl">
        Cataloghiamo {projects.length || 133} progetti open source maturi pensati per la PMI manifatturiera, con guida d'installazione in italiano.
        {sectorFilter && <span className="block mt-1 text-xs">Filtrati per reparto: <strong className="capitalize">{sectorFilter.replace("_", " ")}</strong> · <Link to="/soluzioni" className="underline">rimuovi filtro</Link></span>}
      </p>

      {!activeEsigenza && !search && !sectorFilter && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
          {esigenze.map((e) => {
            const Icon = LucideIcons[e.icon] || Package;
            return (
              <button
                key={e.key}
                onClick={() => setActiveEsigenza(e.key)}
                data-testid={`esigenza-${e.key}`}
                className="adr-card text-left p-5 group"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: "rgba(15,61,107,0.08)" }}>
                    <Icon className="w-5 h-5" style={{ color: "#0F3D6B" }} strokeWidth={1.6} />
                  </div>
                  <span className="text-xs text-[#555555]">{e.count} progetti</span>
                </div>
                <div className="serif text-base font-semibold text-[#1A1A1A] leading-snug">
                  {e.label}
                </div>
                <div className="flex items-center gap-1 text-xs text-[#0F3D6B] mt-3 group-hover:translate-x-0.5 transition-transform">
                  Esplora <ArrowRight className="w-3 h-3" />
                </div>
              </button>
            );
          })}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#555555]" />
          <input
            type="text"
            placeholder="Cerca progetto..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="adr-input pl-9"
            data-testid="oss-search"
          />
        </div>
        <select
          value={activeEsigenza}
          onChange={(e) => setActiveEsigenza(e.target.value)}
          className="adr-input sm:w-72"
          data-testid="oss-esigenza-filter"
        >
          <option value="">Tutte le esigenze</option>
          {esigenze.map((e) => <option key={e.key} value={e.key}>{e.label}</option>)}
        </select>
      </div>

      {activeEsigenza && (
        <div className="text-sm text-[#555555] mb-4">
          Stai esplorando: <strong>{esigenze.find((e) => e.key === activeEsigenza)?.label}</strong>{" "}
          <button onClick={() => setActiveEsigenza("")} className="text-[#C8102E] underline ml-2">Mostra tutte</button>
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => <div key={i} className="adr-skeleton h-44" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map((p) => {
            const d = DIFF_COLORS[p.difficulty] || DIFF_COLORS.media;
            return (
              <Link
                key={p.slug}
                to={`/soluzioni/${p.slug}`}
                className="adr-card p-5 block"
                data-testid={`oss-card-${p.slug}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <Package className="w-5 h-5 text-[#0F3D6B]" />
                  <span className="adr-badge capitalize" style={{ background: d.bg, color: d.color }}>
                    {p.difficulty}
                  </span>
                </div>
                <h3 className="serif text-base font-semibold text-[#1A1A1A] mb-1 leading-snug">{p.name}</h3>
                <p className="text-xs text-[#555555] mb-2 line-clamp-3">{p.short_description_it}</p>
                <div className="text-xs text-[#555555] font-mono">{p.license}</div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
